import { useContext, useRef, useState } from 'react';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { AlbumAddContentTypes, VaultNavigationTabs } from 'common/enums';
import { NewEntryModal } from 'common/new-entry-modal';
import { AppContext } from 'context/app-context';
import { VaultDetailContext } from 'context/vault-detail-context';
import { useCreateAlbum } from 'pages/vault-detail/hooks/use-create-album';
import { NewAlbumModal } from 'pages/vault-detail/components/vault-top-bar/components/new-album-modal';
import { VideoCallWaitingRoom } from 'pages/vault-detail/components/video-call-waiting-room';
import { excludeDefaultAlbum } from 'helpers/utils';
import { keepPickedFolder } from 'helpers/picked-folder';
import { goToPage, RouteName } from 'routes';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import EmptyDashboardBackgroundImage from 'assets/images/empty-state-dashboard.png';
import { AudioWavesIcon, PlusIcon, VideoIcon } from 'assets/icons';
import {
  FolderInput,
  ImportSourceTiles,
  ImportSourceKey,
} from 'common/import-source-tiles';
import styles from './empty-dashboard-state.module.scss';

export const EmptyDashboardState = () => {
  const {
    state: { vaultId, answeredAlbums, unansweredAlbums },
  } = useContext(VaultDetailContext);
  const { state: appState } = useContext(AppContext);
  const vault = appState.currentVault;
  const createAlbum = useCreateAlbum();
  const [showNewAlbumModal, setShowNewAlbumModal] = useState(false);
  // Recording an entry uses the same modal as the top-bar "New +" menu.
  const [newEntryMode, setNewEntryMode] = useState<AlbumAddContentTypes | null>(
    null,
  );
  // "Start an interview" is the video-call flow the planner also opens.
  const [showVideoCall, setShowVideoCall] = useState(false);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const albumOptions = excludeDefaultAlbum([
    ...answeredAlbums,
    ...unansweredAlbums,
  ]).map((album) => ({ id: album.id, name: album.name }));

  // Same flow as the top-bar "New +" → New Album: ask for a name, create the
  // album, then open its page.
  const handleCreateNamedAlbum = async (name: string) => {
    setShowNewAlbumModal(false);
    const album = await createAlbum(vaultId, name);
    if (!album) return;
    goToPage(
      RouteName.AlbumScreen,
      { id: vaultId, albumId: album.id },
      {},
      withOrigin({ vaultId, tab: VaultNavigationTabs.albums }),
    );
  };

  // Bulk upload is the main path for most new vaults, so each tile starts its
  // source right away. "Your Computer" opens the folder dialog here (it needs
  // this click) and hands the folder to the import page's review; the others
  // open their own step on the import page.
  const handleSelectSource = (source: ImportSourceKey) => {
    if (source === 'computer') {
      folderInputRef.current?.click();
      return;
    }
    goToPage(RouteName.VaultImport, { id: vaultId }, { source });
  };

  const handleFolderPicked = (files: File[]) =>
    goToPage(
      RouteName.VaultImport,
      { id: vaultId },
      { source: 'computer', pick: keepPickedFolder(files) },
    );

  return (
    <>
      {showNewAlbumModal && (
        <NewAlbumModal
          onSubmit={handleCreateNamedAlbum}
          onClose={() => setShowNewAlbumModal(false)}
        />
      )}

      {newEntryMode && vault && (
        <NewEntryModal
          mode={newEntryMode}
          vault={vault}
          albums={albumOptions}
          onClose={() => setNewEntryMode(null)}
        />
      )}

      {showVideoCall && vault && (
        <VideoCallWaitingRoom
          vault={vault}
          onClose={() => setShowVideoCall(false)}
        />
      )}
      <div className="flex w-full flex-col items-center justify-center">
        <div className="flex w-full max-w-5xl flex-col items-center gap-8">
          {/* The artwork carries a lot of its own transparent margin, so it
              sits flush against the tabs above and the heading below — no
              container gap on either side of it. */}
          <div className="flex w-full flex-col items-center gap-0">
            <div className="flex w-full items-center justify-center">
              <img
                src={EmptyDashboardBackgroundImage}
                alt=""
                className="h-auto max-h-[182px] w-full max-w-6xl object-contain"
              />
            </div>
            <div className="flex flex-col items-center gap-6 px-4 text-center">
              <h1 className="text-[18px] leading-[27px] font-medium tracking-[-0.44px] text-[#181D27]">
                Start Building Your Vault
              </h1>
              <p className="text__body__regular__medium__gray500 max-w-2xl tracking-[-0.31px]">
                Create albums to capture your memories and experiences, and keep
                them all together in your vault.
              </p>
            </div>
          </div>

          <div className={styles.bulk}>
            <span className={styles.bulkLabel}>
              Bring in everything at once
            </span>
            <ImportSourceTiles onSelect={handleSelectSource} />
            <FolderInput
              inputRef={folderInputRef}
              onPick={handleFolderPicked}
            />
          </div>

          <div className={styles.orRow}>
            <span className={styles.orLine} />
            <span className={styles.orText}>or</span>
            <span className={styles.orLine} />
          </div>

          <div className={styles.record}>
            <span className={styles.bulkLabel}>Capture something new</span>
            <div className={styles.recordActions}>
              <button
                type="button"
                className={styles.recordButton}
                onClick={() => setNewEntryMode(AlbumAddContentTypes.audio)}
              >
                <AudioWavesIcon />
                Record Audio
              </button>
              <button
                type="button"
                className={styles.recordButton}
                onClick={() => setNewEntryMode(AlbumAddContentTypes.video)}
              >
                <VideoIcon />
                Record Video
              </button>
              <button
                type="button"
                className={styles.recordButton}
                onClick={() => setShowVideoCall(true)}
              >
                <VideoIcon />
                Start an Interview
              </button>
            </div>
          </div>

          <div className={styles.orRow}>
            <span className={styles.orLine} />
            <span className={styles.orText}>or</span>
            <span className={styles.orLine} />
          </div>

          <div className="flex flex-col items-center gap-4">
            <Button
              buttonSize={ButtonSize.Medium}
              buttonStyle={ButtonStyle.Primary}
              onClick={() => setShowNewAlbumModal(true)}
              className="flex items-center gap-2"
            >
              <PlusIcon />
              Create Album
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
